import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, Info, AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ToastContext, type ToastApi, type ToastOptions, type ToastTone } from './toast-context';

interface ToastEntry {
  id: number;
  body: string;
  tone: ToastTone;
}

const SUCCESS_DURATION_MS = 4_000;
const INFO_DURATION_MS = 4_000;
const WARNING_DURATION_MS = 5_500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastEntry[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((all) => all.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (body: string, tone: ToastTone = 'success', opts?: ToastOptions) => {
      const id = ++idRef.current;
      setItems((all) => [...all, { id, body, tone }]);
      const ttl =
        opts?.durationMs ??
        (tone === 'error'
          ? 0
          : tone === 'warning'
            ? WARNING_DURATION_MS
            : tone === 'info'
              ? INFO_DURATION_MS
              : SUCCESS_DURATION_MS);
      if (ttl > 0) {
        setTimeout(() => dismiss(id), ttl);
      }
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex flex-col items-center gap-2 px-3 sm:right-6 sm:left-auto sm:items-end"
      >
        {items
          .filter((t) => t.tone !== 'error')
          .map((t) => (
            <ToastCard key={t.id} entry={t} onDismiss={dismiss} />
          ))}
      </div>
      <div
        aria-live="assertive"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex flex-col items-center gap-2 px-3 sm:right-6 sm:left-auto sm:items-end"
      >
        {items
          .filter((t) => t.tone === 'error')
          .map((t) => (
            <ToastCard key={t.id} entry={t} onDismiss={dismiss} />
          ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ entry, onDismiss }: { entry: ToastEntry; onDismiss: (id: number) => void }) {
  const t = entry;
  return (
    <div
      role={t.tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto inline-flex max-w-md items-start gap-2 rounded-lg border bg-surface px-3 py-2 text-sm shadow-lg motion-safe:transition-opacity',
        t.tone === 'success' && 'border-positive/30 text-text-primary',
        t.tone === 'info' && 'border-primary/30 text-text-primary',
        t.tone === 'warning' && 'border-warning/30 text-text-primary',
        t.tone === 'error' && 'border-negative/30 text-text-primary',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'mt-0.5 grid size-5 place-items-center rounded-full',
          t.tone === 'success' && 'bg-positive/15 text-positive',
          t.tone === 'info' && 'bg-primary/15 text-primary',
          t.tone === 'warning' && 'bg-warning/15 text-warning',
          t.tone === 'error' && 'bg-negative/15 text-negative',
        )}
      >
        {t.tone === 'success' && <Check className="size-3" />}
        {t.tone === 'info' && <Info className="size-3" />}
        {(t.tone === 'warning' || t.tone === 'error') && <AlertTriangle className="size-3" />}
      </span>
      <span className="flex-1 leading-snug">{t.body}</span>
      {t.tone === 'error' && (
        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={() => onDismiss(t.id)}
          className="-mr-1 -mt-1 grid size-6 place-items-center rounded-md text-muted-text transition-colors hover:bg-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}
